import { Icon, TextboxInput } from '../../../atoms';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { AdminFilterDropdown, OptionType } from '../Dropdown';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTranslation } from 'react-i18next';
import { checkFFEnabled } from '../../../../utils';
import { FEATURE_NAMES } from '../../../../common/constants';
import { debounce } from 'lodash';

type Props = {
    // eslint-disable-next-line
    onChange(value?: OptionType<string>): void;
    value?: OptionType;
    clients: OptionType<string>[];
};

export const SearchInput = ({ onChange, value, clients }: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isActive = !!anchorEl;
    const { t } = useTranslation();
    const proposalPaginationSearchFFEnabled =
        checkFFEnabled(FEATURE_NAMES['PROPOSALS_PAGINATION_SEARCH_FE_AEX-2382']) || false;

    const [text, setText] = useState('');
    const theme = useTheme();

    useEffect(() => {
        if (proposalPaginationSearchFFEnabled) {
            searchInputRef?.current?.focus?.();
        }
    }, []);

    useEffect(() => {
        setText(value?.label ?? '');
    }, [value]);

    const clientsOptions = clients.filter(({ value }) => value?.toLowerCase().includes(text.toLowerCase()));

    const handleChange = useCallback(
        (value?: OptionType<string>) => {
            onChange(value);

            setAnchorEl(null);
        },
        [onChange]
    );

    const debouncedHandleChange = useMemo(
        () => debounce((value: OptionType<string> | undefined) => handleChange(value), 500),
        [handleChange]
    );

    const clear = () => {
        setText('');
        handleChange(undefined);
    };

    const inputContainerRef = useRef<null | HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleOpen = () => {
        setAnchorEl(inputContainerRef.current);
    };

    return (
        <>
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <div>
                    <InputContainer
                        isDropdownActive={isActive}
                        ref={inputContainerRef}
                        proposalPaginationSearchFFEnabled={proposalPaginationSearchFFEnabled}
                    >
                        <TextboxInput
                            fullWidth
                            inputRef={searchInputRef}
                            onFocus={handleOpen}
                            InputProps={{
                                startAdornment: (
                                    <Icon id="search_ftds" size={16} fill={theme.palette.primary[500]} />
                                ),
                                endAdornment: text?.length ? (
                                    <ClearButton type="button" onClick={clear}>
                                        <Icon id="rounded-close" size={17} />
                                    </ClearButton>
                                ) : null
                            }}
                            value={text}
                            placeholder={t('SEARCH_BY_CLIENT_NAME')}
                            onChange={(e) => {
                                const newText = e.target.value;
                                setText(newText);
                                debouncedHandleChange.cancel();
                                if (newText === '') {
                                    if (proposalPaginationSearchFFEnabled) {
                                        debouncedHandleChange(undefined);
                                    } else {
                                        handleChange(undefined);
                                    }
                                } else {
                                    setAnchorEl(inputContainerRef.current);
                                    if (newText.trim().length > 1 && proposalPaginationSearchFFEnabled) {
                                        debouncedHandleChange({ label: newText, value: newText });
                                    }
                                }
                            }}
                            autoComplete="off"
                        />
                    </InputContainer>

                    {!proposalPaginationSearchFFEnabled && (
                        <AdminFilterDropdown
                            placement="bottom-start"
                            maxHeight={180}
                            onClose={() => {
                                // doesn't do anything on purpose
                            }}
                            options={clientsOptions}
                            onSelectOption={handleChange}
                            filterText={text}
                            selectedOption={value?.value}
                            anchorEl={anchorEl}
                            width={anchorEl?.getBoundingClientRect?.()?.width}
                            disableClickAway
                        />
                    )}
                </div>
            </ClickAwayListener>
        </>
    );
};

const InputContainer = styled('div')<{ isDropdownActive: boolean; proposalPaginationSearchFFEnabled: boolean }>(
    ({ isDropdownActive = false, proposalPaginationSearchFFEnabled = false, theme }: any) => ({
        position: 'relative',
        '& .MuiOutlinedInput-root': {
            padding: '8px 16px',
            gap: '16px',
            borderRadius: '4px',
            backgroundColor: theme.palette.neutral.white,
            border: `1px solid ${theme.palette.neutral[500]}`,
            '&:hover, &.Mui-focused': {
                border: `1px solid ${theme.palette.neutral[500]}`
            }
        },
        '& .MuiInputAdornment-root': {
            margin: 0,
            height: 'auto',
            maxHeight: 'none'
        },
        input: {
            padding: 0,
            height: 'auto',
            color: theme.palette.semantic.text.default,
            fontFamily: '"TT Commons Pro"',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '22px',
            '::placeholder': {
                color: theme.palette.semantic.text.default,
                opacity: 1
            }
        },
        ...(isDropdownActive &&
            !proposalPaginationSearchFFEnabled && {
                zIndex: 130
            })
    })
);

const ClearButton = styled('button')(() => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '17px',
    height: '17px',

    '> svg': {
        width: '17px',
        height: '17px'
    }
}));
